<template>
  <div
    v-if="showExamples"
    class="lighting-advisor-examples"
    data-testid="lighting-advisor-example-objects"
  >
    <AccordionComponent :item-heading-level="4" @click="handleAccordionClick">
      <AccordionItem uid="lighting-advisor-example-objects" :is-open="true">
        <template #title>
          {{
            $textDictionary['lightingAdvisor.examples.show'] ??
            'Show example object'
          }}
        </template>

        <div class="lighting-advisor-examples__body">
          <div
            v-if="step === 1"
            class="lighting-advisor-examples__selection"
            role="radiogroup"
            :aria-label="
              $textDictionary['lightingAdvisor.examples.object.label'] ??
              'Example object'
            "
          >
            <div
              v-for="option in objectOptions"
              :key="option.value"
              class="lighting-advisor-examples__radio"
            >
              <PrimeVueRadioButton
                v-model="selectedObject"
                :input-id="`lighting-advisor-example-${option.value}`"
                :value="option.value"
                name="lighting-advisor-example-object"
              />
              <label :for="`lighting-advisor-example-${option.value}`">
                {{ option.label }}
              </label>
            </div>
          </div>

          <div
            class="lighting-advisor-examples__images"
            :class="{
              'lighting-advisor-examples__images--single': isSetupSketch
            }"
          >
            <template v-if="loading">
              <LoadingSkeleton
                v-for="index in isSetupSketch ? 1 : 2"
                :key="index"
                class="lighting-advisor-examples__skeleton"
                type="block"
              />
            </template>
            <template v-else-if="beforeImage">
              <figure class="lighting-advisor-examples__image">
                <Media
                  :media="beforeImage"
                  :cols="[12, 12, 12]"
                  :lazyload="true"
                />
                <figcaption v-if="beforeCaption">
                  {{ beforeCaption }}
                </figcaption>
              </figure>
              <figure
                v-if="afterImage"
                class="lighting-advisor-examples__image"
              >
                <Media
                  :media="afterImage"
                  :cols="[12, 12, 12]"
                  :lazyload="true"
                />
                <figcaption>{{ afterCaption }}</figcaption>
              </figure>
            </template>
          </div>
        </div>
      </AccordionItem>
    </AccordionComponent>
  </div>
</template>

<script setup lang="ts">
import {
  AccordionComponent,
  AccordionItem,
  LoadingSkeleton,
  Media
} from '@hypercodestudio/basler-components';
import PrimeVueRadioButton from 'primevue/radiobutton';
import type {
  LightingAdvisorSetupField,
  LightingAnswers
} from '~/types/lightingAdvisor';
import type { LightingAdvisorConfiguration } from '~/utils/lightingAdvisor/configurationSchema';
import {
  type LightingAdvisorExampleObject,
  type LightingAdvisorExampleStep,
  useLightingAdvisorExampleImages
} from '~/composables/useLightingAdvisorExampleImages';

interface Props {
  configuration: LightingAdvisorConfiguration;
  answers: LightingAnswers;
  step: number;
  /** Step 2 only: measurement input with focus, highlighted in the sketch. */
  focusedField?: LightingAdvisorSetupField | null;
}

const props = withDefaults(defineProps<Props>(), { focusedField: null });
const emit = defineEmits<{
  toggle: [state: 'open' | 'close'];
}>();
const { $textDictionary } = useNuxtApp();
const selectedObject = ref<LightingAdvisorExampleObject>('object1');
const examplesOpen = ref(true);

function handleAccordionClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('button')) {
    return;
  }
  emit('toggle', examplesOpen.value ? 'close' : 'open');
  examplesOpen.value = !examplesOpen.value;
}

const objectOptions = computed(() => [
  {
    value: 'object1' as const,
    label:
      $textDictionary.value['lightingAdvisor.examples.object1'] ?? 'Euro coin'
  },
  {
    value: 'object2' as const,
    label:
      $textDictionary.value['lightingAdvisor.examples.object2'] ?? 'Limo bottle'
  },
  {
    value: 'object3' as const,
    label:
      $textDictionary.value['lightingAdvisor.examples.object3'] ??
      'Circuit board'
  }
]);

/** Step 2 shows a single setup sketch instead of a before/after comparison. */
const isSetupSketch = computed(() => props.step === 2);

const spectrumFeature = computed(() =>
  props.answers.a2s3q1 === 'white'
    ? 'white'
    : String(props.answers.a2s3q2 ?? '')
);
const beforeCaption = computed(() => {
  if (props.step === 2) {
    return '';
  }
  return props.step === 3
    ? ($textDictionary.value['lightingAdvisor.examples.spectrum.normal'] ??
        'Normal light')
    : ($textDictionary.value['lightingAdvisor.examples.before'] ??
        'Without selected lighting');
});
const afterCaption = computed(() => {
  if (props.step !== 3 || !spectrumFeature.value) {
    return (
      $textDictionary.value['lightingAdvisor.examples.after'] ??
      'With selected lighting'
    );
  }
  return (
    $textDictionary.value[
      `lightingAdvisor.examples.spectrum.${spectrumFeature.value}`
    ] ?? spectrumFeature.value
  );
});

const configuration = computed(() => props.configuration);
const step = computed<LightingAdvisorExampleStep>(() => {
  if (props.step === 2) {
    return 2;
  }
  return props.step === 3 ? 3 : 1;
});
const answers = computed(() => props.answers);
const focusedField = computed(() => props.focusedField);
const { beforeImage, afterImage, loading } = useLightingAdvisorExampleImages(
  configuration,
  step,
  selectedObject,
  answers,
  focusedField
);

const showExamples = computed(() => {
  const examples = props.configuration.images?.examples;
  if (props.step === 1) {
    return Boolean(props.answers.a2s1q3 && examples?.step1);
  }

  if (props.step === 2) {
    return Boolean(examples?.step2);
  }

  const lightColor = props.answers.a2s3q1;
  const feature = props.answers.a2s3q2;
  if (lightColor === 'white') {
    return Boolean(examples?.step3);
  }
  return Boolean(examples?.step3 && feature && feature !== 'uv');
});
</script>

<style scoped>
.lighting-advisor-examples {
  margin-top: 1.5rem;
}

.lighting-advisor-examples__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* Keeps the comparison readable without dominating the step. */
  max-width: 34rem;
}

.lighting-advisor-examples__selection {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
}

.lighting-advisor-examples__radio {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.lighting-advisor-examples__radio label {
  cursor: pointer;
}

.lighting-advisor-examples__images {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.lighting-advisor-examples__images--single {
  grid-template-columns: minmax(0, 1fr);
}

.lighting-advisor-examples__image {
  min-width: 0;
  margin: 0;
}

.lighting-advisor-examples__image figcaption {
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

.lighting-advisor-examples__skeleton {
  width: 100%;
  aspect-ratio: 3 / 2;
}

.lighting-advisor-examples__images--single
  .lighting-advisor-examples__skeleton {
  aspect-ratio: 1380 / 735;
}
</style>
